import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { Badge, Switch, TableSkeleton } from "../../../components/ui";
import { getTrafficMode, updateTrafficMode } from "../../../services/maintenance";

const errorMessage = (error) => error?.response?.data?.message || error?.message || "";

const EFFECTS = [
  "Comments, counters, home and video pages are kept longer in cache.",
  "When a server process is full, secondary requests (counters, comments, recommendations, playback positions) are refused at once so that opening videos, Premium access, sign-in and payments always go through.",
  "The website saves the playback position every 2 minutes and shows a « High traffic » banner.",
  "Scheduled notifications, unpaid reminders and the JW views sync are postponed until the mode is switched off.",
];

/** Affluence mode: switched on and off by the team only, never automatically. */
const TrafficModeCard = () => {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const apply = (data) => {
    setState(data);
    setMessage(data.message || "");
  };

  // Loaded once (`t` is not a dependency: it may change on every render)
  const tRef = useRef(t);
  tRef.current = t;
  useEffect(() => {
    getTrafficMode()
      .then(apply)
      .catch((error) => toast.error(errorMessage(error) || tRef.current("Error fetching data")));
  }, []);

  const save = (changes, done) => {
    setSaving(true);
    return updateTrafficMode({ message, ...changes })
      .then((data) => {
        apply(data);
        toast.success(done);
      })
      .catch((error) => toast.error(errorMessage(error) || t("Error updating data")))
      .finally(() => setSaving(false));
  };

  if (!state) {
    return (
      <section className="ui-card maint-site">
        <TableSkeleton rows={2} />
      </section>
    );
  }

  return (
    <section className="ui-card maint-site" data-on={state.enabled ? "" : undefined}>
      <div className="maint-task-head">
        <h3>{t("Affluence mode")}</h3>
        {state.enabled ? (
          <Badge color="amber" dot>
            {t("Affluence mode on")}
          </Badge>
        ) : (
          <Badge color="green" dot>
            {t("Normal operation")}
          </Badge>
        )}
      </div>
      <p className="maint-task-text">
        {t("For the day the platform overflows (a video that goes viral). Lightens the load so that the essential keeps working. Takes effect within 5 seconds; never switched on or off automatically.")}
      </p>
      <ul className="maint-effects">
        {EFFECTS.map((effect) => (
          <li key={effect}>{t(effect)}</li>
        ))}
      </ul>

      <Switch
        checked={state.enabled}
        label={t("Affluence mode")}
        onLabel={t("Affluence mode on")}
        offLabel={t("Affluence mode off")}
        onChange={(enabled) => setConfirming(enabled ? "on" : "off")}
      />
      {state.enabled && state.since && (
        <p className="ui-hint">
          {t("Since {{date}}", { date: new Date(state.since).toLocaleString() })}
          {state.by ? ` · ${state.by}` : ""}
        </p>
      )}

      <div className="ui-field">
        <label htmlFor="traffic-message">{t("Banner on the website")}</label>
        <input
          id="traffic-message"
          className="ui-input"
          maxLength={300}
          value={message}
          placeholder={state.defaultMessage}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      {message !== (state.message || "") && (
        <div className="maint-task-actions">
          <button type="button" className="ui-btn ui-btn-secondary" disabled={saving} onClick={() => save({}, t("Saved"))}>
            {t("Save the message")}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirming)}
        onClose={() => setConfirming(null)}
        onConfirm={() => save({ enabled: confirming === "on" }, confirming === "on" ? t("Affluence mode on") : t("Back to normal operation"))}
        confirmLabel={confirming === "on" ? t("Switch on the affluence mode") : t("Back to normal operation")}
      >
        <p>
          {confirming === "on"
            ? t("Switch on the affluence mode? Secondary features may be slower or refused while it is on.")
            : t("Switch off the affluence mode? Postponed tasks (notifications, reminders) start again at their next run.")}
        </p>
      </ConfirmDialog>
    </section>
  );
};

export default TrafficModeCard;
